import React, { useState, useContext, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import { AuthContext } from '../App';
import { getUserAttribute } from '../utils/authUser';
import { apiClient } from '../services/api';

function Settings() {
  const { user, activeCustomerId } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('profile');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [customerProfile, setCustomerProfile] = useState(null);
  const userEmail = getUserAttribute(user, 'email');
  const userCompanyName = getUserAttribute(user, 'custom:company_name');
  const customerId = activeCustomerId || getUserAttribute(user, 'custom:customer_id');

  const [profileData, setProfileData] = useState({
    email: userEmail,
    companyName: userCompanyName
  });

  useEffect(() => {
    setProfileData({
      email: userEmail,
      companyName: userCompanyName
    });
  }, [userEmail, userCompanyName]);

  useEffect(() => {
    let cancelled = false;
    const selectedCustomerId = activeCustomerId || customerId;
    if (!selectedCustomerId) {
      setCustomerProfile(null);
      return;
    }

    const fetchCustomer = async () => {
      try {
        const response = await apiClient.get(`/customers/${selectedCustomerId}`);
        if (!cancelled) {
          setCustomerProfile(response.data || null);
        }
      } catch (error) {
        if (!cancelled) {
          setCustomerProfile(null);
        }
      }
    };

    fetchCustomer();
    return () => {
      cancelled = true;
    };
  }, [activeCustomerId, customerId]);

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const currentUser = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(currentUser, {
        'custom:company_name': profileData.companyName
      });
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to update profile' });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      setSaving(false);
      return;
    }

    try {
      const currentUser = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(
        currentUser,
        passwordData.currentPassword,
        passwordData.newPassword
      );
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to change password' });
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile' },
    { id: 'security', label: 'Security' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'api', label: 'API Keys' }
  ];

  return (
    <div>
      <div className="page-header">
        <h1>Settings</h1>
      </div>

      {/* Tabs */}
      <div style={{ marginBottom: '24px', borderBottom: '1px solid var(--border-color)', display: 'flex', gap: '8px' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '12px 20px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid var(--primary-color)' : '2px solid transparent',
              color: activeTab === tab.id ? 'var(--primary-color)' : 'var(--text-muted)',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Message */}
      {message.text && (
        <div style={{
          padding: '12px 16px',
          borderRadius: 'var(--radius)',
          marginBottom: '24px',
          background: message.type === 'success' ? 'rgba(72, 187, 120, 0.1)' : 'rgba(245, 101, 101, 0.1)',
          color: message.type === 'success' ? 'var(--success-color)' : 'var(--danger-color)',
          border: `1px solid ${message.type === 'success' ? 'var(--success-color)' : 'var(--danger-color)'}`
        }}>
          {message.text}
        </div>
      )}

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="card" style={{ maxWidth: '600px' }}>
          <h2 style={{ marginBottom: '24px' }}>Profile Information</h2>
          <form onSubmit={handleProfileSubmit}>
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={profileData.email}
                disabled
                style={{ background: 'var(--background)' }}
              />
              <p className="help-text">Email cannot be changed</p>
            </div>

            <div className="form-group">
              <label htmlFor="companyName">Company Name</label>
              <input
                type="text"
                id="companyName"
                value={profileData.companyName}
                onChange={(e) => setProfileData({ ...profileData, companyName: e.target.value })}
                placeholder="Your Company"
              />
            </div>

            <div className="form-group">
                <label>Customer ID</label>
                <input
                  type="text"
                  value={customerId || 'Not assigned'}
                  disabled
                  style={{ background: 'var(--background)', fontFamily: 'monospace' }}
                />
            </div>

            <div className="form-group">
              <label>Active Agreement ID</label>
              <input
                type="text"
                value={customerProfile?.active_agreement_id || 'Not assigned'}
                disabled
                style={{ background: 'var(--background)', fontFamily: 'monospace' }}
              />
            </div>

            <div className="form-group">
              <label>Active Offer ID</label>
              <input
                type="text"
                value={customerProfile?.active_offer_identifier || 'Not assigned'}
                disabled
                style={{ background: 'var(--background)', fontFamily: 'monospace' }}
              />
            </div>

            <div className="form-group">
              <label>Marketplace Agreements</label>
              {(customerProfile?.marketplace_agreements || []).length === 0 ? (
                <p className="help-text">No agreement records captured yet.</p>
              ) : (
                <div className="table-wrap" style={{ marginTop: '8px' }}>
                  <table className="table marketplace-agreements-table">
                    <thead>
                      <tr>
                        <th>Agreement ID</th>
                        <th>Offer ID</th>
                        <th>Status</th>
                        <th>LicenseArn</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(customerProfile.marketplace_agreements || []).map((agreement, idx) => (
                        <tr key={`${agreement.agreement_id || agreement.license_arn || agreement.offer_identifier || idx}`}>
                          <td className="marketplace-identifier">{agreement.agreement_id || '-'}</td>
                          <td className="marketplace-identifier">{agreement.offer_identifier || '-'}</td>
                          <td>{agreement.status || '-'}</td>
                          <td className="marketplace-identifier">{agreement.license_arn || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div style={{ maxWidth: '600px' }}>
          <div className="card" style={{ marginBottom: '24px' }}>
            <h2 style={{ marginBottom: '24px' }}>Change Password</h2>
            <form onSubmit={handlePasswordSubmit}>
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <input
                  type="password"
                  id="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  required
                />
                <p className="help-text">Minimum 8 characters with uppercase, lowercase, number, and symbol</p>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  required
                />
              </div>

              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          </div>

          <div className="card">
            <h2 style={{ marginBottom: '16px' }}>Two-Factor Authentication</h2>
            <p style={{ color: 'var(--text-muted)', marginBottom: '16px' }}>
              Add an extra layer of security to your account by enabling two-factor authentication.
            </p>
            <button className="btn btn-secondary">Enable 2FA</button>
          </div>
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="card" style={{ maxWidth: '600px' }}>
          <h2 style={{ marginBottom: '24px' }}>Notification Preferences</h2>

          <div style={{ marginBottom: '20px', padding: '16px', background: 'var(--background)', borderRadius: 'var(--radius)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h4 style={{ marginBottom: '4px' }}>Instance Alerts</h4>
                <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Get notified when your instances have issues</p>
              </div>
              <label className="switch">
                <input type="checkbox" defaultChecked />
                <span className="slider"></span>
              </label>
            </div>
          </div>

          <div style={{ marginBottom: '20px', padding: '16px', background: 'var(--background)', borderRadius: 'var(--radius)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h4 style={{ marginBottom: '4px' }}>Billing Notifications</h4>
                <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Receive invoices and billing updates</p>
              </div>
              <label className="switch">
                <input type="checkbox" defaultChecked />
                <span className="slider"></span>
              </label>
            </div>
          </div>

          <div style={{ padding: '16px', background: 'var(--background)', borderRadius: 'var(--radius)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h4 style={{ marginBottom: '4px' }}>Product Updates</h4>
                <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>News about new features and improvements</p>
              </div>
              <label className="switch">
                <input type="checkbox" />
                <span className="slider"></span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* API Keys Tab */}
      {activeTab === 'api' && (
        <div className="card" style={{ maxWidth: '800px' }}>
          <div className="card-header">
            <h2>API Keys</h2>
            <button className="btn btn-primary">Generate New Key</button>
          </div>
          <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>
            API keys allow you to authenticate with the Virtuoso SaaS API programmatically.
          </p>

          <div className="empty-state">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <h3>No API keys yet</h3>
            <p>Generate an API key to get started with programmatic access</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;
